import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
} from "@angular/forms";
import { DateAdapter } from "@angular/material/core";
import { MatStepper } from "@angular/material/stepper";
import {
  AuthService,
  FlightLleegoService,
  StateService,
  UserService,
} from "@core/services";
import { TranslateService } from "@ngx-translate/core";
import countries from "assets/utils/amadeus/countries.json";
import moment from "moment";
import { Subscription } from "rxjs";
import municipalitiesJson from "../../../../../../assets/utils/resident-municipalities.json";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { UserCardModalComponent } from "@shared/component/user-card-modal/user-card-modal.component";
import { ChangeDetectorRef } from '@angular/core';

@Component({
  selector: "app-flight-step-travelerform",
  templateUrl: "./flight-step-travelerform.component.html",
  styleUrls: ["./flight-step-travelerform.component.scss"],
})
export class FlightStepTravelerformComponent implements OnInit, OnDestroy {
  @Input() totalPrice: number;
  @Input() travelersAge: any[];
  @Input() isUser: boolean;
  @Input() userUuid: string;
  @Input() needAddress: boolean;
  @Input() needDocApis: boolean;
  @Input() stepper: MatStepper;
  @Input() selectedUsers: any;
  @Input() fareSelected: any;
  @Input() selectedFlights: any;
  @Input() isEmployee: any;
  @Input() company: any;
  @Input() userFilters: any;
  @Input() passengerBookingParams: any;
  @Input() travelerFormToRestore: FormArray;
  @Output() travelerFormSubmit: EventEmitter<UntypedFormGroup> =
    new EventEmitter<UntypedFormGroup>();
  user: any;
  private subcriptions: Subscription[] = [];
  department: any;
  birthCountry = "";
  nationality = "";
  date = new Date();
  todayMoment = moment();
  minDateNgStruct = {
    year: this.date.getFullYear() - 120,
    month: this.date.getMonth() + 1,
    day: this.date.getDate(),
  };
  maxDateNgStruct = {
    year: this.date.getFullYear(),
    month: this.date.getMonth() + 1,
    day: this.date.getDate(),
  };
  countries = [];
  munipalitiesList = municipalitiesJson;
  canUseResident: boolean;
  disableAccordionAnimations = true;
  accomInfo;
  dynamicPassengerFields: any[] = [];

  constructor(
    public translate: TranslateService,
    private _formBuilder: UntypedFormBuilder,
    private authService: AuthService,
    private userService: UserService,
    public flightLleegoService: FlightLleegoService,
    private dateAdapter: DateAdapter<Date>,
    private stateService: StateService,
    private modalService: NgbModal,
    private cdr: ChangeDetectorRef
  ) {
    this.dateAdapter.setLocale("en-GB");
  }

  ngOnInit(): void {
    const accom = localStorage.getItem("packageRateAccomInfo");
    if (accom) {
      this.accomInfo = JSON.parse(accom)
    }
    if (this.isUser) {
      this.subcriptions.push(
        this.authService.getProfile().subscribe((u) => {
          this.user = u;
          this.subcriptions.push(
            this.userService
              .getPoliciesUser(this.user["uuid"])
              .subscribe((policy) => {
                this.createFormAndPreFill();
                this.flightLleegoService.travelerForm.controls.forEach((travelerFormGroup, index) => {
                  travelerFormGroup.get('documentType').valueChanges.subscribe(value => {
                    this.updateDocumentNumber(index, value);
                  });
                });
                this.f.valueChanges.subscribe((value) => {
                  this.getInvalidString();
                });
              })
          );
        })
      );
    } else {
      this.subcriptions.push(
        this.userService.getUser(this.userUuid).subscribe((u) => {
          this.user = u;
          this.subcriptions.push(
            this.userService
              .getPoliciesUser(this.user["uuid"])
              .subscribe((policy) => {
                this.createFormAndPreFill();
                this.flightLleegoService.travelerForm.controls.forEach((travelerFormGroup, index) => {
                  travelerFormGroup.get('documentType').valueChanges.subscribe(value => {
                    this.updateDocumentNumber(index, value);
                  });
                });
                this.f.valueChanges.subscribe((value) => {
                  this.getInvalidString();
                });
              })
          );
        })
      );
    }
  }

  private normalizeFieldName(name: string): string {
    return (name || "").toLowerCase().trim();
  }

  private getPassengerFields(): any[] {
    return Array.isArray(this.passengerBookingParams?.fields)
      ? this.passengerBookingParams.fields
      : [];
  }

  hasPassengerField(fieldName: string): boolean {
    const normalized = this.normalizeFieldName(fieldName);
    const aliases = this.getPassengerFieldAliases();
    const acceptedNames = aliases[normalized] || [normalized];

    const fields = this.getPassengerFields();
    if (!fields.length) return true;

    return fields.some((field) =>
      acceptedNames.includes(this.normalizeFieldName(field?.name))
    );
  }

  isPassengerFieldMandatory(fieldName: string): boolean {
    const normalized = this.normalizeFieldName(fieldName);

    const aliases = this.getPassengerFieldAliases();

    const acceptedNames = aliases[normalized] || [normalized];

    const found = this.getPassengerFields().find((field) =>
      acceptedNames.includes(this.normalizeFieldName(field?.name))
    );

    return !!found?.mandatory;
  }

  shouldShowPassengerField(fieldName: string, showByDefault = true): boolean {
    const fields = this.getPassengerFields();
    if (!fields.length) return showByDefault;

    if (fieldName === "lastSurname") {
      return this.hasPassengerField("surname");
    }

    if (fieldName === "documents") {
      return true;
    }

    return this.hasPassengerField(fieldName);
  }

  private getDynamicValidators(
    fieldName: string,
    fallbackValidators: any[] = []
  ): any[] {
    const fields = this.getPassengerFields();

    if (!fields.length) {
      return fallbackValidators;
    }

    const required = this.isPassengerFieldMandatory(fieldName);

    return required ? [Validators.required, ...fallbackValidators] : fallbackValidators;
  }

  private getPassengerFieldAliases(): Record<string, string[]> {
    return {
      name: ["name"],
      surname: ["surname", "lastname", "last_name"],
      lastsurname: ["surname", "surname2", "lastname", "last_name"],
      gender: ["gender", "sex"],
      email: ["email", "mail"],
      phonenumber: ["phone", "phone_number", "mobile", "contact_phone"],
      birthdate: ["birthdate", "birth_date", "date_of_birth"],

      expirydate: [
        "expirydate",
        "expiry_date",
        "expiration_date",
        "document_expiry",
        "document_expiration",
        "document_expiration_date",
        "doc_expiry",
        "doc_expiration_date",
        "doc_expiration"
      ],

      issuancedate: [
        "issuancedate",
        "issuance_date",
        "expedition_date",
        "document_issuance",
        "document_expedition",
        "document_expedition_date",
        "doc_issuance",
        "doc_expedition_date"
      ],

      documenttype: [
        "doc_type",
        "document_type",
        "doctype"
      ],

      documentnumber: [
        "doc_number",
        "document_number",
        "document"
      ],

      expecountry: [
        "issuance_country",
        "document_country",
        "doc_country",
        "expedition_country",
        "doc_expedition_country",
        "document_expedition_country"
      ],

      nationality: ["nationality"],
      city: [
        "city",
        "residence_city",
        "resident_city"
      ],
      documents: ["frequent_traveller", "ff_card", "ff", "documents"]
    };
  }

  createFormAndPreFill() {
    this.countries = countries;
    this.flightLleegoService.travelerForm = this._formBuilder.array([]);

    const birthdateStruct = this.toNgbDateStruct(this.user?.birthdate);

    const expiryDateStruct =
      this.user?.dni ? this.toNgbDateStruct(this.user?.dniExpirationDate)
      : this.user?.passport ? this.toNgbDateStruct(this.user?.passportExpires)
      : '';

    const issuanceDateStruct =
      this.user?.dni ? this.toNgbDateStruct(this.user?.dniIssuanceDate)
      : this.user?.passport ? this.toNgbDateStruct(this.user?.passportIssuanceDate)
      : '';

    this.canUseResident =
      this.user?.resident &&
      this.user.resident.toString() !== "none" &&
      this.stateService.residentCandidates &&
      this.stateService.residentCandidates.find(r => r === this.user.resident);

    this.flightLleegoService.travelerForm.push(
      this._formBuilder.group({
        name: [
          this.user.name ? this.user.name : "",
          this.getDynamicValidators("name", [
            Validators.maxLength(90),
            Validators.minLength(2),
          ]),
        ],
        surname: [
          this.user.lastname ? this.user.lastname.split(" ")[0] : "",
          this.getDynamicValidators("surname", [Validators.maxLength(90)]),
        ],
        lastSurname: [
          this.user.lastname ? this.user.lastname.split(" ")[1] : "",
          [Validators.maxLength(90)],
        ],
        email: [
          this.user.email ? this.user.email : "",
          this.getDynamicValidators("email", [Validators.email]),
        ],
        birthDate: [birthdateStruct, this.getDynamicValidators("birthDate")],
        expiryDate: [expiryDateStruct, this.getDynamicValidators("expiryDate")],
        issuanceDate: [issuanceDateStruct, this.getDynamicValidators("issuanceDate")],
        phoneNumber: [
          this.user.phone ? this.user.phone : "",
          this.getDynamicValidators("phoneNumber"),
        ],
        documentType: [
          this.user?.dni
            ? "IDENTITY_CARD"
            : this.user?.nie
              ? "NIE"
              : this.user?.passport
                ? "PASSPORT"
                : "",
          this.getDynamicValidators("documentType"),
        ],
        documentNumber: [
          this.user.dni
            ? this.user.dni
            : this.user.nie
              ? this.user.nie
              : this.user.passport
                ? this.user.passport
                : "",
          this.getDynamicValidators("documentNumber"),
        ],
        expeCountry: [
          this.user.dni ?
            this.user.country
              ? this.countries.find((c) => c.name === this.user.country)
              : ""
            : this.user.passport ?
              this.user.passportCountryEmitter
                ? this.countries.find((c) => c.name === this.user.passportCountryEmitter)
                : ""
              : "",
          this.getDynamicValidators("expeCountry"),
        ],
        nationality: [
          this.user.nationality
            ? this.countries.find((c) => c.name === this.user.nationality)
            : "",
          this.getDynamicValidators("nationality"),
        ],
        city: [
          this.canUseResident
            ? this.munipalitiesList.find((c) => c.municipality === this.user.city)?.municipality
            : this.user.city,
          this.shouldShowPassengerField("city", false)
            ? this.getDynamicValidators("city", [this.checkResidentCity(this.user)])
            : [],
        ],
        gender: [
          this.user.gender
            ? this.user.gender.charAt(0)?.toUpperCase() +
              this.user.gender.slice(1)
            : "",
          this.getDynamicValidators("gender"),
        ],
        documents: this._formBuilder.group({
          documentType: ["FF"],
          ffCard: this._formBuilder.array(this.initFfCards(this.user)),
          selectedFfCard: [-1]
        }),
        uuid: [this.user && this.user.uuid ? this.user.uuid : ""],
      })
    );

    this.selectedUsers.forEach((user, index) => {
      if (index === 0) return;

    const birthStruct = this.toNgbDateStruct(user?.birthdate);

    const expiryStruct =
      user?.dni ? this.toNgbDateStruct(user?.dniExpirationDate)
      : user?.passport ? this.toNgbDateStruct(user?.passportExpires)
      : '';

    const issuanceStruct =
      user?.dni ? this.toNgbDateStruct(user?.dniIssuanceDate)
      : user?.passport ? this.toNgbDateStruct(user?.passportIssuanceDate)
      : '';

    const canUseResident =
      user?.resident &&
      user.resident.toString() !== "none" &&
      this.stateService.residentCandidates &&
      this.stateService.residentCandidates.find(r => r === user.resident);

      this.flightLleegoService.travelerForm.push(
        this._formBuilder.group({
          name: [
            user.name ? user.name : "",
            this.getDynamicValidators("name", [
              Validators.maxLength(90),
              Validators.minLength(2),
            ]),
          ],
          surname: [
            user.lastname ? user.lastname.split(" ")[0] : "",
            this.getDynamicValidators("surname", [Validators.maxLength(90)]),
          ],
          lastSurname: [
            user.lastname ? user.lastname.split(" ")[1] : "",
            [Validators.maxLength(90)],
          ],
          email: [
            user.email ? user.email : "",
            this.getDynamicValidators("email", [Validators.email]),
          ],
          birthDate: [birthStruct, this.getDynamicValidators("birthDate")],
          expiryDate: [expiryStruct, this.getDynamicValidators("expiryDate")],
          issuanceDate: [issuanceStruct, this.getDynamicValidators("issuanceDate")],
          phoneNumber: [
            user.phone ? user.phone : "",
            this.getDynamicValidators("phoneNumber"),
          ],
          documentType: [
            user?.dni
              ? "IDENTITY_CARD"
              : user?.nie
                ? "NIE"
                : user?.passport
                  ? "PASSPORT"
                  : "",
            this.getDynamicValidators("documentType"),
          ],
          documentNumber: [
            user.dni
              ? user.dni
              : user.nie
                ? user.nie
                : user.passport
                  ? user.passport
                  : "",
            this.getDynamicValidators("documentNumber"),
          ],
          expeCountry: [
            user.dni ?
              user.country
                ? this.countries.find((c) => c.name === user.country)
                : ""
              : user.passport ?
                user.passportCountryEmitter
                  ? this.countries.find((c) => c.name === user.passportCountryEmitter)
                  : ""
                : "",
            this.getDynamicValidators("expeCountry"),
          ],
          nationality: [
            user.nationality
              ? this.countries.find((c) => c.name === user.nationality)
              : "",
            this.getDynamicValidators("nationality"),
          ],
          city: [
            canUseResident
              ? this.munipalitiesList.find((c) => c.municipality === user.city)?.municipality
              : "",
            this.shouldShowPassengerField("city", false)
              ? this.getDynamicValidators("city", [this.checkResidentCity(user)])
              : [],
          ],
          gender: [
            user.gender
              ? user.gender.charAt(0)?.toUpperCase() +
              user.gender.slice(1)
              : "",
            this.getDynamicValidators("gender"),
          ],
          documents: this._formBuilder.group({
            documentType: ["FF"],
            ffCard: this._formBuilder.array(this.initFfCards(user)),
            selectedFfCard: [null]
          }),
          uuid: [user && user.uuid ? user.uuid : ""],
        })
      );
    });
    if (this.travelerFormToRestore) {
      this.flightLleegoService.travelerForm.controls.forEach((control: FormGroup) => {
        const previous = this.travelerFormToRestore.controls.find(
          (traveler) => traveler.get('uuid').value === control.get('uuid').value
        );
        if (!previous) return;

        const documents = control.get('documents') as FormGroup;
        documents.setControl('ffCard', this._formBuilder.array(
          this.initFfCards({ ffCard: previous.get('documents.ffCard').getRawValue() })
        ));
        control.patchValue(previous.getRawValue(), { emitEvent: false });
      });
    }
    this.getInvalidString();
    this.cdr.detectChanges();
    setTimeout(() => {
      this.disableAccordionAnimations = false;
    }, 0);
  }

  initFfCards(user: any): FormGroup[] {
    if (user && user.ffCard && Array.isArray(user.ffCard)) {
      return user.ffCard
        .filter(card => card && card.cardName && card.cardNumber)
        .map(card =>
          this._formBuilder.group({
            cardName: [card.cardName, Validators.required],
            cardNumber: [card.cardNumber, Validators.required]
          })
        );
    } else {
      return [];
    }
  }

  getFfCardsArray(index: number): FormArray {
    const travelerFormGroup = this.flightLleegoService.travelerForm.at(index) as FormGroup;
    if (!travelerFormGroup) {
      return null;
    }
    const documentsGroup = travelerFormGroup.get('documents') as FormGroup;
    if (!documentsGroup) {
      return null;
    }
    return documentsGroup.get('ffCard') as FormArray;
  }
  
  getSelectedFfCardControl(index: number): FormControl {
    const travelerFormGroup = this.flightLleegoService.travelerForm.at(index) as FormGroup;
    if (!travelerFormGroup) {
      return null;
    }
  
    const documentsGroup = travelerFormGroup.get('documents') as FormGroup;
    if (!documentsGroup) {
      return null;
    }
    return documentsGroup.get('selectedFfCard') as FormControl;
  }

  openFfCardModal(index) {
    const traveller = this.flightLleegoService.travelerForm.at(index).value.uuid;

    const modalRef = this.modalService.open(UserCardModalComponent, {
      windowClass: 'custom-modal-user-card',
      centered: true,
      backdrop: 'static',
      keyboard: false
    });
  
    modalRef.componentInstance.ffCards = this.flightLleegoService.travelerForm.at(index).value.documents.ffCard;
    modalRef.componentInstance.user = traveller;
    modalRef.componentInstance.cardType = 'ffCard';
  
    modalRef.result.then((result) => {
      if (result) {
        let { ffCards, selectedFfCard } = result;

        ffCards = ffCards.filter(card => card !== undefined && card !== null);
        if (selectedFfCard >= ffCards.length) {
          selectedFfCard = 0;
        }
  
        const ffCardArray = this.flightLleegoService.travelerForm.at(index).get('documents').get('ffCard') as FormArray;
        const selectedCard = ffCards[selectedFfCard];
        ffCards.splice(selectedFfCard, 1);
        ffCards.unshift(selectedCard);
        ffCardArray.clear();
        ffCards.forEach(card => {
          ffCardArray.push(this._formBuilder.group({
            cardName: [card.cardName, Validators.required],
            cardNumber: [card.cardNumber, Validators.required]
          }));
        });
  
        const selectedFfCardControl = this.flightLleegoService.travelerForm.at(index).get('documents').get('selectedFfCard') as FormControl;
        selectedFfCardControl.setValue(0);
      }
    }).catch((error) => {});
  }
  
  get f() {
    return this.flightLleegoService.travelerForm;
  }

  getInvalidString(): string {
    let response: string = ``;

    const keys = [
      "flight.result-flight.form.name",
      "flight.result-flight.form.surname",
      "flight.result-flight.form.lastSurname",
      "flight.result-flight.form.email",
      "flight.result-flight.form.birthDate",
      "flight.result-flight.form.phoneNumber",
      "flight.result-flight.form.documentNumber",
      "flight.result-flight.form.expeCountry",
      "flight.result-flight.form.nationality",
      "flight.result-flight.form.documentType",
      "flight.result-flight.form.fillForContinue",
      "flight.form-flight.passenger",
      "user.form.expiryDate",
      "user.form.issuanceDate",
      "user.form.invalid-birthdate",
      "common.city",
      "user.details.gender",
    ];
    this.translate.get(keys).subscribe((result) => {
      this.f.controls.forEach((formControl: UntypedFormGroup) => {
        if (formControl.invalid) {
          response += ` ${result["flight.form-flight.passenger"]} Nº ${this.f.controls.indexOf(formControl) + 1
            }  ${result["flight.result-flight.form.fillForContinue"]} `;
        }
        let invalids = Object.keys(formControl.controls).filter((key) => {
          if (!formControl.controls[key].invalid) return false;

          const visibilityMap = {
            name: this.shouldShowPassengerField("name"),
            surname: this.shouldShowPassengerField("surname"),
            lastSurname: this.shouldShowPassengerField("lastSurname"),
            email: this.shouldShowPassengerField("email"),
            birthDate: this.shouldShowPassengerField("birthDate"),
            phoneNumber: this.shouldShowPassengerField("phoneNumber"),
            documentNumber: this.shouldShowPassengerField("documentNumber"),
            expeCountry: this.shouldShowPassengerField("expeCountry"),
            nationality: this.shouldShowPassengerField("nationality"),
            documentType: this.shouldShowPassengerField("documentType"),
            expiryDate: this.shouldShowPassengerField("expiryDate"),
            issuanceDate: this.shouldShowPassengerField("issuanceDate"),
            city: this.shouldShowPassengerField("city", false),
            gender: this.shouldShowPassengerField("gender"),
          };

          return visibilityMap[key] ?? true;
        });
        invalids.forEach((key, index) => {
          switch (key) {
            case "name":
              if (invalids[index] === invalids[invalids.length - 1]) {
                response += `${result["flight.result-flight.form.name"]}.`;
              } else {
                response += `${result["flight.result-flight.form.name"]},`;
              }
              break;
            case "surname":
              if (invalids[index] === invalids[invalids.length - 1]) {
                response += `${result["flight.result-flight.form.surname"]}.`;
              } else {
                response += `${result["flight.result-flight.form.surname"]}, `;
              }
              break;
            case "lastSurname":
              if (invalids[index] === invalids[invalids.length - 1]) {
                response += `${result["flight.result-flight.form.lastSurname"]}. `;
              } else {
                response += `${result["flight.result-flight.form.lastSurname"]}, `;
              }
              break;
            case "gender":
              if (invalids[index] === invalids[invalids.length - 1]) {
                response += `${result["user.details.gender"]}. `;
              } else {
                response += `${result["user.details.gender"]}, `;
              }
              break;
            case "email":
              if (invalids[index] === invalids[invalids.length - 1]) {
                response += `${result["flight.result-flight.form.email"]}. `;
              } else {
                response += `${result["flight.result-flight.form.email"]}, `;
              }
              break;
            case "birthDate":
              if (invalids[index] === invalids[invalids.length - 1]) {
                response += `${result["flight.result-flight.form.birthDate"]}. `;
              } else {
                response += `${result["flight.result-flight.form.birthDate"]}, `;
              }
              break;
            case "phoneNumber":
              if (invalids[index] === invalids[invalids.length - 1]) {
                response += `${result["flight.result-flight.form.phoneNumber"]}. `;
              } else {
                response += `${result["flight.result-flight.form.phoneNumber"]}, `;
              }
              break;
            case "documentNumber":
              if (invalids[index] === invalids[invalids.length - 1]) {
                response += `${result["flight.result-flight.form.documentNumber"]}. `;
              } else {
                response += `${result["flight.result-flight.form.documentNumber"]}, `;
              }
              break;
            case "expeCountry":
              if (invalids[index] === invalids[invalids.length - 1]) {
                response += `${result["flight.result-flight.form.expeCountry"]}. `;
              } else {
                response += `${result["flight.result-flight.form.expeCountry"]}, `;
              }
              break;
            case "nationality":
              if (invalids[index] === invalids[invalids.length - 1]) {
                response += `${result["flight.result-flight.form.nationality"]}. `;
              } else {
                response += `${result["flight.result-flight.form.nationality"]}, `;
              }
              break;
            case "documentType":
              if (invalids[index] === invalids[invalids.length - 1]) {
                response += `${result["flight.result-flight.form.documentType"]}. `;
              } else {
                response += `${result["flight.result-flight.form.documentType"]}, `;
              }
              break;
            case "expiryDate":
              if (invalids[index] === invalids[invalids.length - 1]) {
                response += `${result["user.form.expiryDate"]}. `;
              } else {
                response += `${result["user.form.expiryDate"]}, `;
              }
              break;
            case "issuanceDate":
              if (invalids[index] === invalids[invalids.length - 1]) {
                response += `${result["user.form.issuanceDate"]}. `;
              } else {
                response += `${result["user.form.issuanceDate"]}, `;
              }
              break;
            case "city":
              if (invalids[index] === invalids[invalids.length - 1]) {
                response += `${result["common.city"]}. `;
              } else {
                response += `${result["common.city"]}, `;
              }
              break;
            default:
              break;
          }
        });
      });
    });
    return response;
  }

  goNextStep() {
    if (!this.flightLleegoService?.travelerForm || this.flightLleegoService.travelerForm.invalid) {
      this.flightLleegoService?.travelerForm?.markAllAsTouched();
      this.getInvalidString();
      return;
    }

    this.flightLleegoService.passengerBookingParams = this.passengerBookingParams;
    this.stepper.next();
  }

  // checkAgesCorrect() {
  //   let correctAges = 0;
  //   this.travelersAge.forEach((travelerAge) => {
  //     if (
  //       this.flightLleegoService.travelerForm.value.some((traveler) => {
  //         const travelerRealAge = travelerAge.age ?? travelerAge;
  //         const birthDate = moment({
  //           year: traveler.birthDate.year,
  //           month: traveler.birthDate.month - 1,
  //           day: traveler.birthDate.day,
  //         });
  //         const age = this.todayMoment.diff(birthDate, "years");
  //         return age === travelerRealAge;
  //       })
  //     ) {
  //       correctAges++;
  //     }
  //   });
  //   return correctAges === this.travelersAge.length;
  // }

  ngOnDestroy() {
    this.subcriptions.forEach((s) => s.unsubscribe());
  }

  checkResidentCity(user: any) {
    return (control: AbstractControl): ValidationErrors | null => {
      const canUseResident =
        user?.resident &&
        user.resident.toString() !== "none" &&
        this.stateService.residentCandidates &&
        this.stateService.residentCandidates.find((r) => r === user.resident);

      if (!canUseResident) {
        return null;
      }

      if (!control.value && !this.isPassengerFieldMandatory("city")) {
        return null;
      }

      const municipality = this.munipalitiesList.find(
        (c) => c.municipality === control.value
      );

      if (municipality && municipality.community === user.resident) {
        return null;
      }

      return { city: true };
    };
  }

  private updateDocumentNumber(index: number, documentType: string) {
    const traveler = this.flightLleegoService.travelerForm.at(index);
    if (index === 0) {
      switch (documentType?.toUpperCase()) {
        case 'IDENTITY_CARD':
          traveler.get('documentNumber').setValue(this.user.dni || '');
          traveler.get('issuanceDate').setValue(this.parseDate(this.user.dniIssuanceDate) || '');
          traveler.get('expiryDate').setValue(this.parseDate(this.user.dniExpirationDate) || '');
          traveler.get('expeCountry').setValue(this.parseCountry(this.user.country) || '');
          break;
        case 'NIE':
          traveler.get('documentNumber').setValue(this.user.nie || '');
          traveler.get('issuanceDate').setValue(this.parseDate(this.user.nieIssuanceDate) || '');
          traveler.get('expiryDate').setValue(this.parseDate(this.user.nieExpirationDate) || '');
          traveler.get('expeCountry').setValue(this.parseCountry(this.user.country) || '');
          break;
        case 'PASSPORT':
          traveler.get('documentNumber').setValue(this.user.passport || '');
          traveler.get('issuanceDate').setValue(this.parseDate(this.user.passportIssuanceDate) || '');
          traveler.get('expiryDate').setValue(this.parseDate(this.user.passportExpires) || '');
          traveler.get('expeCountry').setValue(this.parseCountry(this.user.passportCountryEmitter) || '');
          break;
        default:
          break;
      }
    }
  }

  parseCountry(countrySelected: string) {
    if (!countrySelected) {
      return null;
    }
    return this.countries.find((c) => c.name === countrySelected)
  }

  parseDate(dateString: string) {
    if (!dateString) {
      return null;
    }
    const parts = dateString.split("-");
    return {
      year: parseInt(parts[0], 10),
      month: parseInt(parts[1], 10),
      day: parseInt(parts[2], 10)
    };
  }

  private toNgbDateStruct(dateStr?: string | null) {
    if (!dateStr) return '';
    const m = moment(dateStr);
    if (!m.isValid()) return '';
    return { year: m.year(), month: m.month() + 1, day: m.date() };
  }

  getTotalPriceAccom() {
    let totalPrice = 0;
    this.accomInfo.selectedRooms.forEach((room) => {
      room.rates.forEach((rate) => {
        totalPrice += rate.price && rate.price.totalPrice ? Number(rate.price.totalPrice.total) : Number(rate.totalWithPercentage);
      })
    })
    return totalPrice;
  }

  getTotalPackage() {
    return this.fareSelected.total + this.getTotalPriceAccom()
  }

  isPriceWithinRangeMethod(totalPrice: number): boolean {
    if (
      !this.userFilters ||
      this.userFilters?.priceMinFlight == null ||
      this.userFilters?.priceMaxFlight == null
    ) {
      return true;
    }
  
    return (
      totalPrice >= this.userFilters.priceMinFlight &&
      totalPrice <= this.userFilters.priceMaxFlight
    );
  }

  shouldShowResidentCityField(): boolean {
    return this.shouldShowPassengerField("city", false);
  }

}
